import {
  ClearableBy,
  ErrorCode,
  Severity,
  type Fault,
} from '@sb-protogen/external/external_control';

import { buildFault } from '@sb/external-control-signals';
import { ZERO_POSE, ZERO_ROTATION, type CartesianPose } from '@sb/geometry';
import * as log from '@sb/log';
import {
  PrimitiveEventType,
  TCState,
  type ControllerStateMsg,
  type Primitive,
  type PrimitiveEventMsg,
  type ServoL,
} from '@sb/messenger/auto_generated/messenger/trajectory_controller_service';
import type { TrajectoryControllerConnector } from '@sb/messenger/connectors/generated';
import type { SubscriptionToken } from '@sb/messenger/node';

import {
  enqueueWithTcPrerequisites,
  type TcPrerequisiteSource,
} from '../../enqueueWithTcPrerequisites';
import { composeCartesianFrames } from '../../util/tcFrames';
import {
  operatorFaultMessage,
  tcPrimitiveFault,
  tcRejectionFault,
} from '../../util/tcOperatorFaultMessage';

import {
  resolveServoConfig,
  type ExternalControlServoConfig,
  type ResolvedServoConfig,
  type TcServoModality,
} from './Arguments';

const ns = log.namespace('TcServoSession');

// The TC's control-loop rate, used to convert the customer-facing smoothing
// time constant (tauMs) into the TC's per-tick low-pass gain:
// k_p = 1 − exp(−dt/τ). Must match [control].loop_hz in tc-tuning.toml.
const TC_CONTROL_LOOP_HZ = 1000;

/** Convert the customer-facing τ (ms) to the TC wire gain. */
function tauMsToKp(tauMs: number): number {
  const dtMs = 1000 / TC_CONTROL_LOOP_HZ;

  return 1 - Math.exp(-dtMs / tauMs);
}

const ENQUEUE_TIMEOUT_MS = 3000;
const SET_SAFETY_SESSION_TIMEOUT_MS = 3000;
const STOP_RPC_TIMEOUT_MS = 1000;
const CLEAR_ERROR_TIMEOUT_MS = 1000;
const CLEAR_QUEUE_TIMEOUT_MS = 1000;
const DEFAULT_IDLE_WAIT_TIMEOUT_MS = 2000;

/**
 * Optional servo settings for the enqueue. Any omitted field falls back to
 * `DEFAULT_SERVO_CONFIG`. Sourced from the customer `start()` request. The
 * tuning fields apply to either primitive; `referenceFrame` is pose-only and
 * ignored by ServoJ.
 */
export type ServoConfig = ExternalControlServoConfig;

/** Human-facing name of the primitive each modality enqueues, for logs and
 * fault reasons — the TC names it this way too. */
const PRIMITIVE_NAME: Record<TcServoModality, string> = {
  joints: 'ServoJ',
  pose: 'ServoL',
};

/** What the client is streaming, for the staleness fault a customer reads. */
const STREAM_LABEL: Record<TcServoModality, string> = {
  joints: 'Joint',
  pose: 'Pose',
};

export interface TcServoSessionOptions {
  /** Selects the primitive; defaults to `joints`. */
  modality?: TcServoModality;
  /**
   * Called once when the session ends without a stop(): `PRIMITIVE_FAILED`, a
   * `TC_ERROR` state, or an unexpected `PRIMITIVE_COMPLETE` — the servo command
   * timeout reports as a successful completion.
   */
  onFault?: (fault: Fault) => void;
  /** Max time to wait for the TC to reach IDLE during stop() before proceeding. */
  idleWaitTimeoutMs?: number;
  /** Per-session servo tuning; omitted fields fall back to the defaults. */
  servoConfig?: ServoConfig;
  /**
   * Active tool's flange→TCP transform, used as ServoL's `commanded_frame`.
   * Identity, the default, leaves the flange as the controlled point. Unused by
   * the joints modality.
   */
  flangeToTcp?: CartesianPose;
}

/**
 * Drives the trajectory-controller servo control-plane for a TC-routed external
 * streaming session — ServoJ for a joint stream, ServoL for a pose stream. The
 * high-rate target stream (`WriteServoJ` / `WriteServoL`) is pushed by
 * external-control-bot; this owns only the lifecycle RPCs:
 *
 *  - start(): `ClearError` (defensive; a prior session may have left ERROR) →
 *    `Enqueue(ServoJ | ServoL)` → subscribe `ControllerState`/`PrimitiveEvent`.
 *  - stop():  `Stop` → wait for `TC_IDLE` → `ClearQueue` → `ClearError` if the
 *    TC was left in ERROR → unsubscribe.
 *
 * The lease is owned by the External Control step, acquired before start() and
 * released only after stop() reaches IDLE — releasing mid-STOPPING forces a
 * spurious TC error. Faults are reported via `onFault`; this never auto-recovers.
 */
export class TcServoSession {
  private primitiveId: number | undefined;

  private subscribed = false;

  /** Tokens for this session's own topic registrations, so teardown drops only
   * them: the same `(serviceKey, topic)` keys are shared with botman's
   * long-lived TC signal consumer, and a no-token unsubscribe would silence it
   * for the life of the process. */
  private controllerStateToken: SubscriptionToken | undefined;

  private primitiveEventToken: SubscriptionToken | undefined;

  private latestState: TCState = TCState.TC_IDLE;

  private faulted = false;

  /** Set by stop(); checked after every await in start() so a stop landing
   * mid-start never leaves a live primitive or subscription behind. */
  private stopRequested = false;

  private startPromise: Promise<void> | null = null;

  /** True once start() settled (either way); lets stop() skip the wait-out. */
  private startSettled = false;

  private stopPromise: Promise<void> | null = null;

  private readonly idleWaiters: Array<() => void> = [];

  private readonly idleWaitTimeoutMs: number;

  private readonly onFault: (fault: Fault) => void;

  private readonly servoConfig: ResolvedServoConfig;

  private readonly modality: TcServoModality;

  private readonly flangeToTcp: CartesianPose;

  constructor(
    private readonly connector: TrajectoryControllerConnector,
    private readonly prereqSource: TcPrerequisiteSource,
    options: TcServoSessionOptions = {},
  ) {
    this.onFault = options.onFault ?? (() => {});

    this.idleWaitTimeoutMs =
      options.idleWaitTimeoutMs ?? DEFAULT_IDLE_WAIT_TIMEOUT_MS;

    this.servoConfig = resolveServoConfig(options.servoConfig);

    this.modality = options.modality ?? 'joints';

    this.flangeToTcp = options.flangeToTcp ?? ZERO_POSE;
  }

  /**
   * Enqueue the servo primitive and begin observing the TC. Must run after the
   * `TRAJECTORY_CONTROLLER` lease is acquired so the primitive seeds its filter
   * to the real current position / pose.
   */
  public start(): Promise<void> {
    this.startPromise ??= this.doStart().finally(() => {
      this.startSettled = true;
    });

    return this.startPromise;
  }

  private async doStart(): Promise<void> {
    // ClearError before Enqueue on (re)start — a prior session may have left
    // the TC in ERROR, and the TC rejects an Enqueue from ERROR.
    // Idempotent: a no-op when already IDLE.
    await this.connector.ClearError({}, CLEAR_ERROR_TIMEOUT_MS);

    if (this.stopRequested) {
      return;
    }

    const reply = await enqueueWithTcPrerequisites(
      this.connector,
      this.buildPrimitive(),
      this.prereqSource,
      {
        enqueueTimeoutMs: ENQUEUE_TIMEOUT_MS,
        setSafetySessionTimeoutMs: SET_SAFETY_SESSION_TIMEOUT_MS,
        isCancelled: () => this.stopRequested,
      },
    );

    if (this.stopRequested) {
      // A stop landed while the enqueue was in flight. stop() is awaiting this
      // promise and tears down whatever was enqueued — don't subscribe or treat
      // the session as live.
      log.warn(
        ns`start`,
        `Stopped during ${this.primitiveName} enqueue; skipping subscribe`,
      );

      this.primitiveId = reply.accepted ? reply.primitiveId : undefined;

      return;
    }

    if (!reply.accepted) {
      // The thrown message is operator-facing and deliberately says nothing
      // about which stream it was; the log is where a reader tells a ServoJ
      // rejection from a ServoL one, and sees the code behind the sentence.
      log.warn(ns`start`, `${this.primitiveName} enqueue rejected`, {
        error: reply.error,
      });

      throw new Error(operatorFaultMessage(tcRejectionFault(reply.error)));
    }

    this.primitiveId = reply.primitiveId;

    // Subscribe AFTER Enqueue — the RPC triggers service discovery so the topic
    // ports are cached and the SUB sockets connect immediately.
    this.controllerStateToken = this.connector.subscribeToControllerState(
      this.onControllerState.bind(this),
    );

    this.primitiveEventToken = this.connector.subscribeToPrimitiveEvent(
      this.onPrimitiveEvent.bind(this),
    );

    this.subscribed = true;

    log.info(ns`start`, `${this.primitiveName} session enqueued`, {
      primitiveId: this.primitiveId,
    });
  }

  /** Name the TC gives the primitive this session drives; used in every log
   * line and fault reason so a reader can tell the two streams apart. */
  private get primitiveName(): string {
    return PRIMITIVE_NAME[this.modality];
  }

  /**
   * The enqueue carries settings only — targets arrive exclusively via the sink
   * (`WriteServoJ` / `WriteServoL`), and the primitive holds the current
   * commanded position or pose until the first streamed target lands.
   */
  private buildPrimitive(): Primitive {
    const settings = {
      kP: tauMsToKp(this.servoConfig.tauMs),
      commandTimeoutMs: this.servoConfig.commandTimeoutMs,
      rampInDurationMs: this.servoConfig.rampInDurationMs,
    };

    if (this.modality === 'pose') {
      return {
        servoL: {
          ...settings,
          // The streamed twist resolves along the same axes the pose is
          // expressed in, so the reference frame carries no rotation of its own.
          ...composeCartesianFrames(
            {
              parent: this.servoConfig.referenceFrame,
              rotationInParent: ZERO_ROTATION,
            },
            this.flangeToTcp,
          ),
          ...this.motionScalingLimits(),
        },
      };
    }

    return {
      servoJ: {
        ...settings,
        // ServoJ-only: `message ServoL` has no equivalent field. Passed in ms;
        // the TC derives its own per-tick gain, unlike tauMs.
        positionSmoothingTimeConstantMs:
          this.servoConfig.positionSmoothingTimeConstantMs,
      },
    };
  }

  /** ServoL's optional per-joint caps, passed straight through (absent = no limit). */
  private motionScalingLimits():
    | Pick<ServoL, 'motionScalingLimits'>
    | undefined {
    const { motionScalingLimits } = this.servoConfig;

    return motionScalingLimits ? { motionScalingLimits } : undefined;
  }

  /**
   * Normal teardown: decelerate to IDLE, drop any pending queue entries, and
   * clear any ERROR the stop itself induced (a Stop landing on an unauthorized
   * TC parks it in ERROR instead of decelerating). The caller must not release
   * the lease until this resolves — releasing mid-STOPPING forces a spurious
   * TC error.
   *
   * Idempotent: concurrent callers share one teardown.
   */
  public stop(): Promise<void> {
    this.stopRequested = true;
    this.stopPromise ??= this.doStop();

    return this.stopPromise;
  }

  private async doStop(): Promise<void> {
    // Wait out an in-flight start() before tearing down, so a just-enqueued
    // primitive is stopped here rather than orphaned; its own failure (if any)
    // surfaces to start()'s caller, not here. Skipped when start already
    // settled so the Stop RPC goes out immediately.
    if (!this.startSettled && this.startPromise) {
      await this.startPromise.catch(() => {});
    }

    try {
      const reply = await this.connector.Stop({}, STOP_RPC_TIMEOUT_MS);
      this.latestState = reply.state;

      if (this.latestState !== TCState.TC_IDLE) {
        await this.waitForIdle();
      }
    } catch (err) {
      log.warn(ns`stop`, `${this.primitiveName} session Stop RPC failed`, {
        err,
      });
    }

    // Best effort even when the Stop RPC failed — dropping pending queue
    // entries is independent of the decel, and skipping it would leave the
    // queue populated after the lease is released.
    try {
      const reply = await this.connector.ClearQueue({}, CLEAR_QUEUE_TIMEOUT_MS);

      this.latestState = reply.state;
    } catch (err) {
      log.warn(ns`stop`, `${this.primitiveName} session ClearQueue failed`, {
        err,
      });
    }

    // Leave the TC as we found it: IDLE. A Stop issued while the TC is
    // unauthorized (the arm safety-stopped mid-session) parks it in ERROR
    // rather than decelerating to IDLE, and nothing outside this session
    // clears that state unless the routine ends in Failure and is recovered —
    // TC jogging has no ClearError of its own, so an ERROR left behind here
    // bricks jogging until the next streaming session starts. Safe at this
    // point: the arm is already stopped and any fault reason was delivered
    // via onFault before teardown began.
    if (this.latestState === TCState.TC_ERROR) {
      try {
        await this.connector.ClearError({}, CLEAR_ERROR_TIMEOUT_MS);
      } catch (err) {
        log.warn(
          ns`stop`,
          `${this.primitiveName} session teardown ClearError failed`,
          { err },
        );
      }
    }

    this.unsubscribe();
  }

  private waitForIdle(): Promise<void> {
    return new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        log.warn(ns`waitForIdle`, 'Timed out waiting for TC_IDLE; proceeding');
        resolve();
      }, this.idleWaitTimeoutMs);

      this.idleWaiters.push(() => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  private onControllerState(msg: ControllerStateMsg): void {
    this.latestState = msg.state;

    // IDLE (target reached) or ERROR (can't reach IDLE) both release stop()'s
    // wait so it doesn't hang.
    if (msg.state === TCState.TC_IDLE || msg.state === TCState.TC_ERROR) {
      const waiters = this.idleWaiters.splice(0);
      for (const resolve of waiters) resolve();
    }

    if (msg.state === TCState.TC_ERROR) {
      // No typed code to map here: this path's only payload is `errors`, which
      // buildStateMsg() never populates. TC_CONTROLLER_ERROR exists for exactly
      // this — the controller is in an error state and told us nothing else.
      this.fault(
        buildFault(
          ErrorCode.TC_CONTROLLER_ERROR,
          Severity.FAULTED,
          ClearableBy.CLEARABLE_BY_CLIENT,
        ),
        'controller state TC_ERROR',
      );
    }
  }

  private onPrimitiveEvent(event: PrimitiveEventMsg): void {
    // The event topic is a global bus — filter by our primitive id.
    if (
      event.primitiveId !== undefined &&
      this.primitiveId !== undefined &&
      event.primitiveId !== this.primitiveId
    ) {
      return;
    }

    if (event.type === PrimitiveEventType.PRIMITIVE_FAILED) {
      this.fault(
        tcPrimitiveFault(event.error),
        `${this.primitiveName} primitive failed: error=${event.error}`,
      );

      return;
    }

    // Neither servo primitive completes on its own except through the command
    // timeout — a session stop decelerates through STOPPING and emits
    // PRIMITIVE_STOPPED instead. The TC reports the timeout as a *successful*
    // completion, so without this the session dies silently: the routine keeps
    // running with the lease held while every subsequent streamed target lands
    // in a mailbox no primitive reads. Skip once our own stop is in flight — a
    // timeout racing the stop is not a fault, the session is ending anyway.
    if (
      event.type === PrimitiveEventType.PRIMITIVE_COMPLETE &&
      !this.stopRequested
    ) {
      // There is no TCErrorCode to map here: the TC reports the timeout as a
      // *successful* completion (which is load-bearing — see the comment
      // above), so this event carries no error code at all. CLIENT_STREAM_TIMEOUT
      // is the code for it. Not CONTROLLER_UNAVAILABLE: that carve-out is for a
      // different timeout — the control loop failing to answer an enqueue within
      // ENQUEUE_TIMEOUT — and must not be reused here.
      this.fault(
        buildFault(
          ErrorCode.CLIENT_STREAM_TIMEOUT,
          Severity.FAULTED,
          ClearableBy.CLEARABLE_BY_CLIENT,
        ),
        `${STREAM_LABEL[this.modality]} streaming timeout (${this.servoConfig.commandTimeoutMs} ms) exceeded.`,
      );
    }
  }

  /**
   * `reason` is for the robot-side log only — it carries internal detail (the
   * raw code, the configured timeout) that has no place in a customer-facing
   * record. What leaves here is the typed `Fault`.
   */
  private fault(fault: Fault, reason: string): void {
    if (this.faulted) {
      return;
    }

    this.faulted = true;

    log.error(ns`fault`, `${this.primitiveName} session faulted`, {
      reason,
      code: fault.code,
      severity: fault.severity,
    });

    this.onFault(fault);
  }

  private unsubscribe(): void {
    if (!this.subscribed) {
      return;
    }

    this.subscribed = false;

    try {
      this.connector.unsubscribeFromControllerState(
        '',
        this.controllerStateToken,
      );
    } catch (err) {
      log.warn(ns`unsubscribe`, 'Failed to unsubscribe from ControllerState', {
        err,
      });
    } finally {
      this.controllerStateToken = undefined;
    }

    try {
      this.connector.unsubscribeFromPrimitiveEvent(
        '',
        this.primitiveEventToken,
      );
    } catch (err) {
      log.warn(ns`unsubscribe`, 'Failed to unsubscribe from PrimitiveEvent', {
        err,
      });
    } finally {
      this.primitiveEventToken = undefined;
    }
  }
}
